import { addMonths, addYears, format, isValid, parseISO } from "date-fns";

import { parseNumericValue } from "./numberFormatter";
import { buildZar8UnlockPositionOptions } from "./zar8LockFlow";

const DEFAULT_POSITION_ASSET_SYMBOL = "ZAR8";

const normalizeDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === "number") {
    const nextDate = new Date(value);
    return isValid(nextDate) ? nextDate : null;
  }

  if (typeof value === "string") {
    const parsedDate = parseISO(value);

    if (isValid(parsedDate)) {
      return parsedDate;
    }

    const fallbackDate = new Date(value);
    return isValid(fallbackDate) ? fallbackDate : null;
  }

  return null;
};

const getDurationParts = (entry) => {
  const period =
    entry?.productId?.duration?.period ??
    entry?.productId?.durationInYearOrMonth?.[0] ??
    entry?.duration?.period;
  const unit =
    entry?.productId?.duration?.unit ??
    entry?.productId?.durationInYearOrMonth?.[1] ??
    entry?.duration?.unit;
  const normalizedPeriod = Number.parseInt(`${period || ""}`, 10);

  return {
    period: Number.isFinite(normalizedPeriod) ? normalizedPeriod : 0,
    unit: typeof unit === "string" ? unit.trim().toLowerCase() : "",
  };
};

const getEntryStartDate = (entry) =>
  normalizeDateValue(
    entry?.startDate ??
      entry?.createdAt ??
      entry?.productId?.startDate ??
      entry?.productId?.createdAt
  );

const getEntryMaturityDate = (entry) => {
  const explicitDate = normalizeDateValue(
    entry?.maturityDate ??
      entry?.endDate ??
      entry?.productId?.maturityDate ??
      entry?.productId?.endDate
  );

  if (explicitDate) {
    return explicitDate;
  }

  const startDate = getEntryStartDate(entry);

  if (!startDate) {
    return null;
  }

  const { period, unit } = getDurationParts(entry);

  if (period <= 0 || !unit) {
    return null;
  }

  if (unit.startsWith("month")) {
    return addMonths(startDate, period);
  }

  if (unit.startsWith("year")) {
    return addYears(startDate, period);
  }

  return null;
};

const formatChartPointLabel = (value) => {
  const dateValue = normalizeDateValue(value);

  if (!dateValue) {
    return {
      fullLabel: value || "",
      shortLabel: value || "",
    };
  }

  return {
    fullLabel: format(dateValue, "d MMM yyyy"),
    shortLabel: format(dateValue, "EEE"),
  };
};

const buildYieldOverviewEventPayload = (payload = {}) => ({
  product: "zar8_yield",
  ...payload,
});

export const emitZar8YieldOverviewEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const eventPayload = buildYieldOverviewEventPayload(payload);

  if (window.analytics?.track) {
    window.analytics.track(eventName, eventPayload);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...eventPayload,
    });
  }

  window.dispatchEvent(
    new CustomEvent("zar8-yield-overview", {
      detail: {
        eventName,
        payload: eventPayload,
      },
    })
  );
};

export const buildZar8YieldActivePositions = (entries = []) => {
  const unlockOptions = buildZar8UnlockPositionOptions(entries);

  return unlockOptions.map((option, index) => {
    const matchingEntry =
      entries.find(
        (entry) =>
          entry?._id === option.ledgerId ||
          entry?.ledgerId === option.ledgerId ||
          entry?.productId?._id === option.ledgerId
      ) ||
      entries.at(index) ||
      null;

    return {
      id: option.id,
      ledgerId: option.ledgerId,
      productId: matchingEntry?.productId?._id || "",
      title: (
        matchingEntry?.productId?.name ||
        matchingEntry?.name ||
        option.label
      ).toUpperCase(),
      lockedAmount: option.lockedAmount,
      assetSymbol:
        matchingEntry?.asset?.toUpperCase() ||
        matchingEntry?.productId?.assetId?.asset?.toUpperCase() ||
        DEFAULT_POSITION_ASSET_SYMBOL,
      startDate: getEntryStartDate(matchingEntry),
      maturityDate: getEntryMaturityDate(matchingEntry),
      rawEntry: matchingEntry,
    };
  });
};

export const buildZar8YieldPerformanceSeries = (graphObject) => {
  const performanceData = Array.isArray(graphObject?.performanceData)
    ? graphObject.performanceData
    : [];

  return performanceData
    .map((entry, index) => {
      const numericValue = parseNumericValue(
        entry?.value ?? entry?.amount ?? entry?.total
      );
      const labels = formatChartPointLabel(
        entry?.label ?? `Point ${index + 1}`
      );

      return {
        id: `${labels.fullLabel}-${index}`,
        value: numericValue,
        fullLabel: labels.fullLabel,
        shortLabel: labels.shortLabel,
      };
    })
    .filter((entry) => Number.isFinite(entry.value));
};

export const getZar8YieldPerformanceDelta = (series = []) => {
  if (series.length < 2) {
    return {
      deltaValue: 0,
      deltaPercentage: 0,
    };
  }

  const firstValue = parseNumericValue(series[0]?.value);
  const lastValue = parseNumericValue(series[series.length - 1]?.value);
  const deltaValue = lastValue - firstValue;
  const deltaPercentage = firstValue > 0 ? (deltaValue / firstValue) * 100 : 0;

  return {
    deltaValue,
    deltaPercentage,
  };
};

export const getZar8YieldTotalLockedValue = (positions = []) =>
  positions.reduce(
    (totalLockedValue, position) =>
      totalLockedValue + parseNumericValue(position?.lockedAmount),
    0
  );

export const formatZar8YieldOverviewDate = (value) => {
  const normalizedDate = normalizeDateValue(value);

  if (!normalizedDate) {
    return "TBD";
  }

  return format(normalizedDate, "dd MMM yyyy");
};
